import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ProfileHeader from '../components/ProfileHeader';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import {
  cancelBookingRequest,
  getBookingCommercialState,
  getRequesterBookingRequests,
  getRequesterBookings,
  requestBookingCancellation,
  requestBookingReschedule,
} from '../utils/booking';
import {
  calculateDepositAmount,
  createBookingCheckoutSessionScaffold,
  doesBookingRequirePayment,
  getBookingOutstandingBalance,
  getRequesterBookingPayments,
  resolveBookingCoverage,
} from '../utils/bookingPayments';
import {
  getBookingPackageCoverageSummary,
  getUserActivePackages,
  getUserPackageUsages,
} from '../utils/servicePackages';
import {
  downloadBookingICS,
  normalizeBookingCalendarState,
} from '../utils/bookingCalendar';
import {
  canInitiateBookingCheckout,
  canRequestBookingChange,
  canViewBookingCalendarState,
  canViewRequesterBookings,
} from '../utils/platformAccess';

function RequesterBookingsPage() {
  const { currentUser } = useAuth() || {};
  const [loading, setLoading] = useState(true);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingPayments, setBookingPayments] = useState([]);
  const [activePackages, setActivePackages] = useState([]);
  const [packageUsages, setPackageUsages] = useState([]);
  const [bookingMessage, setBookingMessage] = useState('');
  const [changeFormsByBookingId, setChangeFormsByBookingId] = useState({});

  useEffect(() => {
    const load = async () => {
      if (!currentUser?.uid) {
        setBookingRequests([]);
        setBookings([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [nextRequests, nextBookings, nextPackages, nextPackageUsages] = await Promise.all([
          getRequesterBookingRequests(db, currentUser.uid),
          getRequesterBookings(db, currentUser.uid),
          getUserActivePackages(db, currentUser.uid),
          getUserPackageUsages(db, currentUser.uid),
        ]);
        const nextPayments = await getRequesterBookingPayments(db, currentUser.uid);
        setBookingRequests(nextRequests || []);
        setBookings(nextBookings || []);
        setBookingPayments(nextPayments || []);
        setActivePackages(nextPackages || []);
        setPackageUsages(nextPackageUsages || []);
        setChangeFormsByBookingId(
          (nextBookings || []).reduce((acc, booking) => {
            const bookingId = booking.bookingId || booking.id;
            if (!bookingId) return acc;
            acc[bookingId] = {
              requestedStartAt: booking.startAt || '',
              requestedEndAt: booking.endAt || '',
              note: '',
            };
            return acc;
          }, {})
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUser?.uid]);

  if (!canViewRequesterBookings(currentUser?.uid || '')) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <ProfileHeader />
        <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-950">My Bookings</h1>
          <p className="mt-3 text-sm text-slate-600">Sign in to view your booking requests and confirmed sessions.</p>
        </section>
      </div>
    );
  }

  const pendingRequests = bookingRequests.filter((bookingRequest) => bookingRequest.status === 'pending');
  const activeBookings = bookings.filter((booking) => ['confirmed', 'upcoming', 'in_progress'].includes(booking.status));
  const historyBookings = bookings.filter((booking) => !['confirmed', 'upcoming', 'in_progress'].includes(booking.status));
  const remainingSessions = activePackages.reduce((sum, membership) => sum + (membership.remainingSessions || 0), 0);
  const remainingCredits = activePackages.reduce((sum, membership) => sum + (membership.remainingCredits || 0), 0);

  const refreshBookings = async () => {
    if (!currentUser?.uid) return;
    const [nextRequests, nextBookings, nextPackages, nextPackageUsages] = await Promise.all([
      getRequesterBookingRequests(db, currentUser.uid),
      getRequesterBookings(db, currentUser.uid),
      getUserActivePackages(db, currentUser.uid),
      getUserPackageUsages(db, currentUser.uid),
    ]);
    const nextPayments = await getRequesterBookingPayments(db, currentUser.uid);
    setBookingRequests(nextRequests || []);
    setBookings(nextBookings || []);
    setBookingPayments(nextPayments || []);
    setActivePackages(nextPackages || []);
    setPackageUsages(nextPackageUsages || []);
  };

  const updateChangeForm = (bookingId, updates = {}) => {
    setChangeFormsByBookingId((current) => ({
      ...current,
      [bookingId]: {
        ...(current[bookingId] || {}),
        ...updates,
      },
    }));
  };

  const handleCancelPendingRequest = async (bookingRequest) => {
    try {
      await cancelBookingRequest(db, {
        bookingRequest,
        handledByUserId: currentUser?.uid || '',
        actorUserId: currentUser?.uid || '',
        actorName: currentUser?.email || '',
      });
      setBookingMessage('Booking request canceled.');
      await refreshBookings();
    } catch (error) {
      setBookingMessage(error?.message || 'Could not cancel the booking request.');
    }
  };

  const handleRequestBookingChange = async (booking, changeType) => {
    const bookingId = booking.bookingId || booking.id;
    const formValues = changeFormsByBookingId[bookingId] || {};

    try {
      if (changeType === 'cancel') {
        await requestBookingCancellation(db, {
          booking,
          requesterUserId: currentUser?.uid || '',
          requesterName: currentUser?.email || '',
          note: formValues.note || '',
          actorUserId: currentUser?.uid || '',
          actorName: currentUser?.email || '',
        });
        setBookingMessage('Cancellation request submitted.');
      } else {
        await requestBookingReschedule(db, {
          booking,
          requestedStartAt: formValues.requestedStartAt || booking.startAt,
          requestedEndAt: formValues.requestedEndAt || booking.endAt,
          requesterUserId: currentUser?.uid || '',
          requesterName: currentUser?.email || '',
          note: formValues.note || '',
          actorUserId: currentUser?.uid || '',
          actorName: currentUser?.email || '',
        });
        setBookingMessage('Reschedule request submitted.');
      }
      await refreshBookings();
    } catch (error) {
      setBookingMessage(error?.message || 'Could not submit the booking change request.');
    }
  };

  const handleCreateCheckout = async (booking, paymentType = 'full') => {
    try {
      const amount = paymentType === 'deposit'
        ? calculateDepositAmount(booking)
        : getBookingOutstandingBalance(
            booking,
            bookingPayments.filter((payment) => payment.bookingId === (booking.bookingId || booking.id))
          );

      const result = await createBookingCheckoutSessionScaffold(db, {
        booking,
        paymentType,
        amount,
        currency: getBookingCommercialState(booking)?.currency || 'USD',
        actorUserId: currentUser?.uid || '',
        actorName: currentUser?.email || '',
      });
      setBookingMessage(`Checkout session prepared${result?.providerCheckoutUrl ? `: ${result.providerCheckoutUrl}` : '.'}`);
      await refreshBookings();
    } catch (error) {
      setBookingMessage(error?.message || 'Could not prepare checkout right now.');
    }
  };

  const handleDownloadCalendar = (booking) => {
    const result = downloadBookingICS(booking);
    setBookingMessage(
      result?.filename
        ? `Calendar file prepared: ${result.filename}`
        : 'Calendar file prepared.'
    );
  };

  return (
    <div className="mx-auto max-w-5xl p-6">
      <ProfileHeader />
      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Bookings</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">My Booking Status</h1>
            <p className="mt-2 text-sm text-slate-600">
              Track submitted booking requests, confirmed sessions, and recent booking history.
            </p>
          </div>
          <Link to="/discover" className="text-sm font-medium text-blue-600 hover:underline">
            Explore public profiles
          </Link>
        </div>
        {bookingMessage ? (
          <p className="mt-4 rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700">{bookingMessage}</p>
        ) : null}

        <div className="mt-5 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-700">Pending Requests</p>
            <p className="mt-2 text-3xl font-bold text-amber-900">{pendingRequests.length}</p>
          </div>
          <div className="rounded-2xl bg-emerald-50 p-4">
            <p className="text-sm font-medium text-emerald-700">Confirmed Bookings</p>
            <p className="mt-2 text-3xl font-bold text-emerald-900">{activeBookings.length}</p>
          </div>
          <div className="rounded-2xl bg-slate-100 p-4">
            <p className="text-sm font-medium text-slate-700">History</p>
            <p className="mt-2 text-3xl font-bold text-slate-950">{historyBookings.length}</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-950">Packages and Memberships</h2>
              <p className="mt-1 text-sm text-slate-600">
                View active prepaid coverage, remaining sessions or credits, and recent package usage.
              </p>
            </div>
            <p className="text-sm text-slate-600">
              {remainingSessions} sessions remaining · {remainingCredits} credits remaining
            </p>
          </div>
          {activePackages.length === 0 ? (
            <p className="mt-3 text-sm text-slate-600">No active package coverage yet.</p>
          ) : (
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {activePackages.map((membership) => (
                <div key={membership.customerMembershipId} className="rounded-xl border border-slate-200 bg-white p-3">
                  <p className="text-sm font-semibold text-slate-900">{membership.packageId || 'Package coverage'}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {membership.remainingSessions} sessions remaining · {membership.remainingCredits} credits remaining
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{membership.status}</p>
                </div>
              ))}
            </div>
          )}
          {packageUsages.length > 0 ? (
            <p className="mt-3 text-xs text-slate-500">
              {packageUsages.filter((usage) => usage.status === 'applied').length} coverage use{packageUsages.filter((usage) => usage.status === 'applied').length === 1 ? '' : 's'} currently applied.
            </p>
          ) : null}
        </div>

        {loading ? (
          <p className="mt-5 text-sm text-slate-600">Loading bookings...</p>
        ) : (
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 p-4">
              <h2 className="text-lg font-semibold text-slate-950">Booking Requests</h2>
              {bookingRequests.length === 0 ? (
                <p className="mt-3 text-sm text-slate-600">No booking requests submitted yet.</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {bookingRequests.map((bookingRequest) => (
                    <div key={bookingRequest.bookingRequestId} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-sm font-semibold text-slate-900">{bookingRequest.serviceType || 'Session request'}</p>
                      <p className="mt-1 text-sm text-slate-600">{bookingRequest.status}</p>
                      <p className="mt-1 text-xs text-slate-500">{bookingRequest.requestedStartAt || 'Start TBD'} · {bookingRequest.timezone}</p>
                      {bookingRequest.status === 'pending' ? (
                        <button
                          onClick={() => handleCancelPendingRequest(bookingRequest)}
                          className="mt-3 rounded-xl bg-rose-100 px-3 py-2 text-sm font-medium text-rose-900"
                        >
                          Cancel Request
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <h2 className="text-lg font-semibold text-slate-950">Confirmed and Recent Bookings</h2>
              {bookings.length === 0 ? (
                <p className="mt-3 text-sm text-slate-600">No confirmed bookings yet.</p>
              ) : (
                <div className="mt-3 space-y-3">
                  {bookings.map((booking) => (
                    <div key={booking.bookingId} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <p className="text-sm font-semibold text-slate-900">{booking.serviceType || 'Session'}</p>
                      <p className="mt-1 text-sm text-slate-600">{booking.status}</p>
                      <p className="mt-1 text-xs text-slate-500">{booking.startAt || 'Start TBD'} · {booking.timezone}</p>
                      {(() => {
                        const bookingId = booking.bookingId || booking.id;
                        const paymentsForBooking = bookingPayments.filter((payment) => payment.bookingId === bookingId);
                        const coverage = resolveBookingCoverage(booking) || {
                          covered: false,
                          sourceType: '',
                          sourceId: '',
                          reason: '',
                        };
                        const packageCoverageSummary = getBookingPackageCoverageSummary(booking, {
                          memberships: activePackages,
                        }) || {
                          covered: false,
                          partial: false,
                          coverageAmount: 0,
                          status: 'none',
                          remainingAmountDue: 0,
                        };
                        const calendarState = normalizeBookingCalendarState(booking) || {
                          syncStatus: 'not_connected',
                          calendarProvider: '',
                          syncErrorMessage: '',
                        };
                        const amountDue = getBookingOutstandingBalance(booking, paymentsForBooking);
                        const depositDue = booking.depositRequired ? calculateDepositAmount(booking) : 0;
                        const requiresPayment = doesBookingRequirePayment(booking);

                        return (
                          <>
                            <p className="mt-1 text-xs text-slate-500">
                              Payment: {getBookingCommercialState(booking)?.paymentStatus || 'unpaid'}
                              {booking.requesterChangeRequest?.status ? ` · Change request: ${booking.requesterChangeRequest.status}` : ''}
                            </p>
                            {canViewBookingCalendarState(booking, { currentUserId: currentUser?.uid || '' }) ? (
                              <p className="mt-1 text-xs text-slate-500">
                                Calendar sync: {calendarState.syncStatus}
                                {calendarState.calendarProvider ? ` · ${calendarState.calendarProvider}` : ''}
                              </p>
                            ) : null}
                            {calendarState.syncErrorMessage ? (
                              <p className="mt-1 text-xs text-rose-600">Sync issue: {calendarState.syncErrorMessage}</p>
                            ) : null}
                            <p className="mt-1 text-xs text-slate-500">
                              {coverage.covered
                                ? `Coverage: ${coverage.reason || coverage.sourceType || 'covered'}`
                                : `Outstanding: ${amountDue} ${getBookingCommercialState(booking)?.currency || 'USD'}`}
                              {packageCoverageSummary.partial ? ` · Package applied: ${packageCoverageSummary.coverageAmount} ${(getBookingCommercialState(booking)?.currency || 'USD')}` : ''}
                              {booking.depositRequired ? ` · Deposit: ${depositDue} ${(getBookingCommercialState(booking)?.currency || 'USD')}` : ''}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                onClick={() => handleDownloadCalendar(booking)}
                                className="rounded-xl bg-sky-100 px-3 py-2 text-sm font-medium text-sky-900"
                              >
                                Add to Calendar
                              </button>
                            </div>
                            {canInitiateBookingCheckout(booking, { currentUserId: currentUser?.uid || '' }) && requiresPayment ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {booking.depositRequired ? (
                                  <button
                                    onClick={() => handleCreateCheckout(booking, 'deposit')}
                                    className="rounded-xl bg-emerald-100 px-3 py-2 text-sm font-medium text-emerald-900"
                                  >
                                    Pay Deposit
                                  </button>
                                ) : null}
                                <button
                                  onClick={() => handleCreateCheckout(booking, 'full')}
                                  className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                                >
                                  Pay Now
                                </button>
                              </div>
                            ) : null}
                          </>
                        );
                      })()}
                      {canRequestBookingChange(booking, { currentUserId: currentUser?.uid || '' }) &&
                      ['confirmed', 'upcoming'].includes(booking.status) ? (
                        <div className="mt-3 space-y-3 rounded-xl border border-slate-200 bg-white p-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Request booking change</p>
                          <div className="grid gap-3 md:grid-cols-2">
                            <input
                              value={changeFormsByBookingId[booking.bookingId]?.requestedStartAt || ''}
                              onChange={(event) => updateChangeForm(booking.bookingId, { requestedStartAt: event.target.value })}
                              type="datetime-local"
                              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            />
                            <input
                              value={changeFormsByBookingId[booking.bookingId]?.requestedEndAt || ''}
                              onChange={(event) => updateChangeForm(booking.bookingId, { requestedEndAt: event.target.value })}
                              type="datetime-local"
                              className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                            />
                            <textarea
                              value={changeFormsByBookingId[booking.bookingId]?.note || ''}
                              onChange={(event) => updateChangeForm(booking.bookingId, { note: event.target.value })}
                              rows={2}
                              placeholder="Optional note for the organization or instructor"
                              className="rounded-xl border border-slate-200 px-3 py-2 text-sm md:col-span-2"
                            />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleRequestBookingChange(booking, 'reschedule')}
                              className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white"
                            >
                              Request Reschedule
                            </button>
                            <button
                              onClick={() => handleRequestBookingChange(booking, 'cancel')}
                              className="rounded-xl bg-rose-100 px-3 py-2 text-sm font-medium text-rose-900"
                            >
                              Request Cancellation
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default RequesterBookingsPage;
